// lyrics.js — synced lyrics for the now-playing view, from LRCLIB.
//
// Spotify has no lyrics API (its lyrics are licensed from Musixmatch for its
// own apps), so the panel asks LRCLIB (lrclib.net): free, keyless, allows
// browser calls (Access-Control-Allow-Origin: *). Checked 2026-10-31: synced
// lyrics for "Better Be Home Soon"; one lookup of three answered 503, so
// failures are expected and say so rather than retry.
//
// Timing follows the Sonos position (media_position + time since
// media_position_updated_at, the same arithmetic as the Scrubber). A
// different recording (remaster, live) can drift; the duration check below
// picks the closest match.

const API = 'https://lrclib.net/api/search';

// "Flame Trees - 2011 Remaster" → "Flame Trees"; "(feat. X)", "[Live]" gone.
export function cleanTitle(title) {
  return String(title || '')
    .replace(/\s*[([][^)\]]*(remaster|live|version|edit|mix|mono|stereo|feat\.?|ft\.?|with )[^)\]]*[)\]]/gi, '')
    .replace(/\s+-\s+.*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function cleanArtist(artist) {
  // Not split on '&': band names carry it (Hunters & Collectors).
  return String(artist || '').split(/,| feat\.? | ft\.? /i)[0].trim();
}

// LRC text → [{ t: seconds, text }] sorted by time. A line may carry
// several stamps ("[00:12.00][01:30.00]chorus"); metadata tags are skipped.
export function parseLrc(lrc) {
  const out = [];
  for (const raw of String(lrc || '').split(/\r?\n/)) {
    const stamps = [...raw.matchAll(/\[(\d{1,3}):(\d{2})(?:[.:](\d{1,3}))?\]/g)];
    if (!stamps.length) continue;
    const text = raw.replace(/\[[^\]]*\]/g, '').trim();
    for (const m of stamps) {
      const frac = m[3] ? Number(`0.${m[3]}`) : 0;
      out.push({ t: Number(m[1]) * 60 + Number(m[2]) + frac, text });
    }
  }
  return out.sort((a, b) => a.t - b.t);
}

// Index of the line being sung at `sec` (last line starting at or before
// it), or -1 before the first line.
export function lineAt(lines, sec) {
  let lo = 0, hi = (lines?.length || 0) - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid].t <= sec) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
}

// Best LRCLIB result for a track: synced lyrics within 3 s of the playing
// duration, then within 8 s, then any synced, then plain. Instrumental
// results answer "no words" rather than "not found".
export function pickResult(results, durationSec) {
  const list = (results || []).filter(Boolean);
  if (!list.length) return null;
  const near = (r, tol) => !durationSec || (r.duration && Math.abs(r.duration - durationSec) <= tol);
  const synced = list.filter((r) => r.syncedLyrics);
  return synced.find((r) => near(r, 3)) || synced.find((r) => near(r, 8))
    || list.find((r) => r.plainLyrics && near(r, 8))
    || synced[0] || list.find((r) => r.plainLyrics) || list.find((r) => r.instrumental) || null;
}

// LRCLIB sheds load with 503 "ServerOverloaded, please retry in a moment"
// (two of three identical requests on 2026-10-31, and Ben saw runs of
// failures the same evening). Retry 503/429 three times, honouring
// retry-after, else 1.5 s, 3 s, then 4.5 s (about 10 s in all). At most four
// requests per track, and only while the Lyrics panel is open.
async function fetchWithRetry(url, { tries = 4, wait = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) {
  let res;
  for (let i = 0; i < tries; i++) {
    res = await fetch(url);
    if (res.status !== 503 && res.status !== 429) return res;
    if (i === tries - 1) break;
    const ra = Number(res.headers.get('retry-after'));
    await wait(Number.isFinite(ra) && ra > 0 ? Math.min(ra, 5) * 1000 : 1500 * (i + 1));
  }
  return res;
}

// Session cache keyed by cleaned title + artist; holds results and misses
// (a failure is not cached, so reopening the panel tries again).
const cache = new Map();

// Returns { status: 'synced'|'plain'|'instrumental'|'none', lines?, plain? }.
// Throws on a network or server failure so the caller can say "couldn't
// reach" rather than "no lyrics".
export async function fetchLyrics({ title, artist, durationSec }) {
  const t = cleanTitle(title);
  const a = cleanArtist(artist);
  if (!t) return { status: 'none' };
  const key = `${t}|${a}`.toLowerCase();
  if (cache.has(key)) return cache.get(key);
  const params = new URLSearchParams({ track_name: t });
  if (a) params.set('artist_name', a);
  const res = await fetchWithRetry(`${API}?${params}`);
  if (!res.ok) throw new Error(`LRCLIB ${res.status}`);
  const best = pickResult(await res.json(), durationSec);
  let value;
  if (!best) value = { status: 'none' };
  else if (best.syncedLyrics) value = { status: 'synced', lines: parseLrc(best.syncedLyrics), plain: best.plainLyrics || '' };
  else if (best.plainLyrics) value = { status: 'plain', plain: best.plainLyrics };
  else if (best.instrumental) value = { status: 'instrumental' };
  else value = { status: 'none' };
  cache.set(key, value);
  return value;
}
